<template>
	<div class="WDatepicker" :class="{ error }">
		<WIcon :icon="icon" :color="iconColor" :size="iconSize" class="Icon" :class="{ onlyCalendar, readonly }" />
		<vc-date-picker
			:id="id"
			:value="value"
			:mode="mode"
			:attributes="attrsPicker"
			:themeStyles="themePicker"
			:minDate="min"
			:maxDate="max"
			:isRequired="required"
			:inputProps="inputProps"
			:availableDates="availableDates"
			:disabledDates="disabledDate"
			:popoverVisibility="visibility"
			:paneWidth="width"
			:tintColor="tintColor"
			:showDayPopover="showDayPopover"
			:formats="formats"
			:isDoublePaned="double"
			:popoverDirection="popoverDirection"
			class="Datepicker"
			:class="{ onlyCalendar }"
			v-on="inputListeners"
		/>
		<Transition name="fade" :duration="500">
			<WText v-if="error" class="Error" typo="span" size="10" color="red">
				{{ error }}
			</WText>
		</Transition>
	</div>
</template>

<script>
import VueTypes from "vue-types";
import { merge } from "lodash";
import { attrs, formats, theme } from "./utils";

export default {
	name: "WDatepicker",
	props: {
		id: VueTypes.string,
		error: VueTypes.string.def(""),
		mode: VueTypes.oneOf(["single", "multiple", "range"]).def("single"),
		formats: VueTypes.object.def(formats),
		double: VueTypes.bool.def(false),
		theme: VueTypes.oneOf([Object, null]), // https://vcalendar.io/api/theme-styles.html
		attrs: VueTypes.oneOf([Object, null]), // https://vcalendar.io/api/attribute.html#properties
		width: VueTypes.number.def(320),
		min: VueTypes.oneOfType([Date, String]),
		max: VueTypes.oneOfType([Date, String]),
		value: VueTypes.oneOfType([String, Object, Array, Date]),
		name: VueTypes.string,
		placeholder: VueTypes.string,
		readonly: VueTypes.bool.def(false),
		inputClass: VueTypes.string,
		required: VueTypes.bool.def(false),
		showDayPopover: VueTypes.bool.def(false), // Tooltip with day info
		availableDates: VueTypes.array,
		disabledDate: VueTypes.array,
		icon: VueTypes.string.def("calendar"),
		iconColor: VueTypes.string,
		iconSize: VueTypes.oneOfType([String, Number]).def(16),
		tintColor: VueTypes.string.def("#B3D615"),
		visibility: VueTypes.oneOf(["hover", "focus", "hidden", "visible"]).def("focus"),
		onlyCalendar: VueTypes.bool.def(false),
		popoverDirection: VueTypes.oneOf(["bottom", "top", "left", "right"]).def("bottom")
	},
	data() {
		return {
			themePicker: this.theme || theme,
			attrsPicker: this.attrs || attrs
		};
	},
	computed: {
		inputProps() {
			const { name, placeholder, readonly, inputClass } = this;
			return {
				name,
				class: ["Input", inputClass].join(" "),
				placeholder,
				readonly,
				disabled: readonly
			};
		},
		inputListeners() {
			return merge({}, this.$listeners, {
				// Este "input" es para los campos de fecha normal y el campo de escoger fecha de inicio y fin.
				input: data => {
					if (data instanceof Date) {
						// El campo origen es una fecha normal.
						this.$emit("input", new Date(this.removeTimeUTC(data)));
					} else if (!data && this.mode === "single") {
						// Texto borrado o que no es una fecha.
						this.$emit("input", null);
					} else {
						// El campo es múltiple.
						this.$emit("input", {
							start: new Date(this.removeTimeUTC(data.start)),
							end: new Date(this.removeTimeUTC(data.end))
						});
					}
				},
				"popover-did-appear": data => {
					this.$emit("onShowPopover", data);
				}
			});
		}
	},
	methods: {
		/**
		 * Manda la fecha cuando el campo es de un valor.
		 * @param Date dateOri Fecha original.
		 */
		/*sendSingleDate(dateOri) {
            // Establecer la hora de finalización al final del día.
            //dateOri.setHours(23, 59, 59, 999);
            console.log(dateOri);
            // Elimina la compensación horaria.
            const dateNew = new Date(this.removeTimeUTC(dateOri));
            console.log(dateNew);
            // Se envía la información con la compensación horaria.
            this.$emit('input', dateNew);
        },*/
		/**
		 * Manda la fecha cuando el campo es multiple.
		 * @param Date datesOri Fechas de inicio y fin.
		 */
		/*sendMultipleDate(datesOri) {
			// Establecer la hora de inicio al inicio y la del final al final.
			//datesOri.start.setHours(0,0,0,0);
			//datesOri.end.setHours(23, 59, 59, 999);
			// Elimina la compensación horaria.
			const datesNew = {
				start: new Date(this.removeTimeUTC(datesOri.start)),
				end: new Date(this.removeTimeUTC(datesOri.end))
			};
			// Se envía la información con la compensación horaria.
			this.$emit("input", datesNew);
		},*/
		removeTimeUTC(date) {
			return date.getTime() + Math.abs(date.getTimezoneOffset()) * 60 * 1000;
		}
	}
};
</script>

<style scoped lang="scss">
@import "@/theme/mixins/_global.scss";

.WDatepicker {
	position: relative;

	.Datepicker {
		&::v-deep .Input {
			height: $form-height;
			width: 100%;
			margin-bottom: 15px;
			padding: 0 25px 0 $form-padding;
			border: none;
			border: $form-border;
			border-radius: $border-radius;
			background: transparent;
			color: $form-color !important;
			font-size: $form-font-size;
			font-weight: $form-font-weight;
			box-sizing: border-box;
			transition: border-color 0.5s ease-in-out;
			cursor: pointer;

			@include placeholder {
				color: $form-placeholder-color;
			}
			&:focus {
				border-color: $form-border-color-active;
				outline: none;
			}
			&[readonly],
			&[disabled] {
				background-color: $form-disabled-background;
				cursor: not-allowed;
			}
		}

		&.onlyCalendar {
			&::v-deep .Input {
				display: none;
			}

			&::v-deep .popover-origin {
				position: relative;
			}
		}
	}

	&.error {
		.Datepicker ::v-deep .Input {
			border-color: $danger;
		}
	}
}

.Icon {
	position: absolute;
	top: $form-height / 2;
	right: $form-padding;
	color: $form-placeholder-color;
	padding: 0px;
	transform: translateY(-50%);
	cursor: pointer;

	&.onlyCalendar {
		display: none;
	}

	&.readonly {
		opacity: 0.5;
	}
}
</style>
